import { useEffect, useRef } from 'react';
import { usePedidosStore } from './store';
import type { Pedido } from './types';

interface BusEvento {
  tipo: string;
  payload?: unknown;
  ts?: string;
}

/**
 * Conecta no WS do backend e mantém o store de pedidos sincronizado.
 * Reconecta com backoff exponencial (cap 15s).
 *
 * Filtra eventos pela data atualmente selecionada — pedidos de outros dias
 * só entram no store quando o usuário navega para aquele dia.
 */
export function useLiveSync() {
  // referência mutável: não dependem de subscrição react
  const canceladoRef = useRef(false);

  useEffect(() => {
    canceladoRef.current = false;
    let socket: WebSocket | null = null;
    let tentativa = 0;
    let timer: number | null = null;
    let teveDesconexao = false;

    const conectar = () => {
      if (canceladoRef.current) return;
      const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
      const url = `${proto}//${location.host}/ws`;
      socket = new WebSocket(url);

      socket.addEventListener('open', () => {
        tentativa = 0;
        // se foi reconexão, refaz fetch para pegar eventos perdidos
        if (teveDesconexao) {
          usePedidosStore.getState().carregar();
        }
      });

      socket.addEventListener('message', (e) => {
        try {
          const evento: BusEvento = JSON.parse(e.data);
          if (evento.tipo === 'pedido_criado' || evento.tipo === 'pedido_atualizado') {
            const p = evento.payload as Pedido;
            if (p && typeof p.id === 'number') {
              const state = usePedidosStore.getState();
              const filtro = state.data;
              const diaPedido = (p.criado_em || '').slice(0, 10);
              const jaConhecido = state.pedidos[p.id] != null;
              // Pass-through para pedidos já visíveis: garantia que mudanças de status
              // chegam mesmo se o pedido foi criado em outro dia (drag em "Todos" etc).
              // Filtro de data só vale para CRIAÇÃO + atualizações de pedidos novos.
              if (!jaConhecido && filtro !== 'all' && diaPedido !== filtro) return;
              const itens = Array.isArray(p.itens)
                ? p.itens
                : p.itens_json
                ? JSON.parse(p.itens_json)
                : [];
              state.upsert({ ...p, itens });
            }
          } else if (evento.tipo === 'pedido_excluido') {
            const payload = evento.payload as { id?: number } | undefined;
            if (payload && typeof payload.id === 'number') {
              usePedidosStore.getState().remover(payload.id);
            }
          } else if (evento.tipo === 'rota_gerada' || evento.tipo === 'rota_invalidada') {
            // backend recalculou/invalidou a rota (cron, exclusão de pedido em outra
            // aba, etc) — avisa a RotasPage via CustomEvent para ela re-buscar.
            window.dispatchEvent(
              new CustomEvent('rota:mudou', { detail: { tipo: evento.tipo, payload: evento.payload } }),
            );
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('[useLiveSync] mensagem inválida:', err);
        }
      });

      const reconectar = () => {
        if (canceladoRef.current) return;
        teveDesconexao = true;
        tentativa = Math.min(tentativa + 1, 8);
        const atraso = Math.min(15000, 500 * Math.pow(2, tentativa));
        timer = window.setTimeout(conectar, atraso);
      };

      socket.addEventListener('close', reconectar);
      socket.addEventListener('error', () => {
        socket?.close();
      });
    };

    conectar();

    return () => {
      canceladoRef.current = true;
      if (timer != null) window.clearTimeout(timer);
      socket?.close();
    };
  }, []);
}
